import { useState } from 'react';
import { X, Ban } from 'lucide-react';
import {
  REDSHIFT_TARGET_LABELS,
  redshiftExportTarget,
  type RedshiftExportJob,
} from '@atlas/shared';
import { cancelRedshiftExport } from '../lib/callables.js';
import { formatBytes, formatRows } from '../lib/redshiftExports.js';
import { SourceBadge } from './SourceBadge.js';

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-2 text-xs">
      <span className="w-28 shrink-0 text-[#aeaaa4]">{label}</span>
      <span className="text-[#37352f] font-mono break-all">{value}</span>
    </div>
  );
}

export function RedshiftExportDetailModal({
  job,
  onClose,
}: {
  job: RedshiftExportJob & { id: string };
  onClose: () => void;
}) {
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const live = job.status === 'queued' || job.status === 'running';
  const results = job.tableResults ?? [];
  // Before anything has been unloaded the pre-flight plan is the useful view;
  // afterwards the results supersede it table by table.
  const done = new Set(results.map((r) => r.table));
  const pending = (job.plan ?? []).filter((p) => !done.has(p.table));

  async function handleCancel() {
    setCancelError(null);
    setCancelling(true);
    try {
      await cancelRedshiftExport({ exportId: job.id });
    } catch (err) {
      setCancelError(err instanceof Error ? err.message : 'Could not cancel.');
    } finally {
      setCancelling(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-[620px] max-w-[95vw] max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 pb-3">
          <div>
            <h2 className="text-sm font-semibold text-[#37352f]">
              <span className="font-mono">{job.schema}</span>
              {job.sourceSystem && (
                <span className="ml-2 align-middle">
                  <SourceBadge system={job.sourceSystem} />
                </span>
              )}
              {job.dryRun && (
                <span className="ml-2 px-1.5 py-0.5 rounded bg-[#f1f1ef] text-[10px] font-medium text-[#73726e] align-middle">
                  pre-flight
                </span>
              )}
            </h2>
            <p className="text-xs text-[#aeaaa4] font-mono mt-0.5">
              s3://{job.s3Bucket}/{job.s3Prefix}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {live && (
              <button
                type="button"
                onClick={() => void handleCancel()}
                disabled={cancelling}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-[#e03e3e] border border-[#f5c6c6] rounded hover:bg-[#fbe8e8] disabled:opacity-50 transition-colors"
              >
                <Ban size={13} />
                {cancelling ? 'Stopping…' : 'Stop'}
              </button>
            )}
            <button onClick={onClose} className="text-[#aeaaa4] hover:text-[#37352f]">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="px-5 pb-5 overflow-y-auto flex flex-col gap-4">
          <div className="flex flex-col gap-1.5 rounded border border-[#e9e9e7] bg-[#fafafa] px-3 py-2.5">
            <Field label="Status" value={job.status} />
            <Field label="Read from" value={REDSHIFT_TARGET_LABELS[redshiftExportTarget(job)]} />
            <Field
              label="Started by"
              value={`${job.createdBy.displayName} · ${new Date(job.createdAt).toLocaleString('en-GB')}`}
            />
            <Field label="Max file size" value={`${job.maxFileSizeMb} MB`} />
            {job.includeTables.length > 0 && (
              <Field label="Only" value={job.includeTables.join(', ')} />
            )}
            {job.excludeTables.length > 0 && (
              <Field label="Skipping" value={job.excludeTables.join(', ')} />
            )}
            {job.totals && (
              <Field
                label="Totals"
                value={`${job.totals.tables} tables · ${formatRows(job.totals.rows)} rows · ${formatBytes(job.totals.bytes)}`}
              />
            )}
          </div>

          {job.error && (
            <p className="text-sm text-[#e03e3e] bg-[#fbe8e8] border border-[#f5c6c6] rounded px-3 py-2 whitespace-pre-wrap break-words">
              {job.error}
            </p>
          )}

          {cancelError && (
            <p className="text-xs text-[#e03e3e]">{cancelError}</p>
          )}

          {results.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-[#37352f] mb-2">
                Unloaded ({results.length})
              </h3>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-[#e9e9e7] text-[#aeaaa4]">
                    <th className="py-1.5 pr-3 text-left font-medium">Table</th>
                    <th className="py-1.5 pr-3 text-left font-medium">Files</th>
                    <th className="py-1.5 pr-3 text-left font-medium">Rows</th>
                    <th className="py-1.5 text-left font-medium">Size</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r) => (
                    <tr key={r.table} className="border-b border-[#f1f1ef]">
                      <td className="py-1.5 pr-3 font-mono text-[#37352f]">
                        {r.table}
                        {r.stem !== r.table && (
                          <span className="text-[#aeaaa4]"> → {r.stem}</span>
                        )}
                      </td>
                      <td className="py-1.5 pr-3 tabular-nums text-[#73726e]">
                        {r.chunks}
                      </td>
                      <td className="py-1.5 pr-3 tabular-nums text-[#73726e]">
                        {formatRows(r.rows)}
                      </td>
                      <td className="py-1.5 tabular-nums text-[#73726e]">
                        {formatBytes(r.bytes)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {pending.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-[#37352f] mb-2">
                {job.dryRun ? `In scope (${pending.length})` : `Remaining (${pending.length})`}
              </h3>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-[#e9e9e7] text-[#aeaaa4]">
                    <th className="py-1.5 pr-3 text-left font-medium">Table</th>
                    <th className="py-1.5 text-left font-medium">Rows (approx.)</th>
                  </tr>
                </thead>
                <tbody>
                  {pending.map((p) => (
                    <tr key={p.table} className="border-b border-[#f1f1ef]">
                      <td className="py-1.5 pr-3 font-mono text-[#37352f]">{p.table}</td>
                      <td className="py-1.5 tabular-nums text-[#73726e]">
                        {formatRows(p.rows)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {job.plan === null && live && (
            <p className="text-xs text-[#aeaaa4]">
              Waiting for the pre-flight to list the schema…
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
